'use client';

import { useCallback, useEffect, useState } from 'react';

import type { ScanHistoryItem } from '@/features/meal-distribution/hooks/useContinuousScanner';

/**
 * Exported so the sign-out teardown can sweep it. This history holds scanned
 * employees' names and internal numbers, so it must not outlive a session.
 * See clearClientSessionState.
 */
export const SCAN_HISTORY_STORAGE_KEY = 'meal-distribution-scan-history';
const STORAGE_KEY = SCAN_HISTORY_STORAGE_KEY;
const MAX_HISTORY_ITEMS = 10;
const CLEANUP_THRESHOLD_HOURS = 1; // Cleanup after 1 hour of inactivity
const CLEANUP_INTERVAL = (CLEANUP_THRESHOLD_HOURS * 60 * 60 * 1000) / 3; // 1/3 of the threshold in milliseconds

type PersistedScanData = {
  history: ScanHistoryItem[];
  totalScans: number;
  successfulScans: number;
  lastActivity: string; // ISO timestamp
};

/**
 * Hook for persisted scan history with automatic cleanup
 * - Stores data in localStorage
 * - Automatically cleans up when the newest entry is older than 1 hour
 * - Survives browser restarts and refreshes
 */
export function usePersistedScanHistory() {
  const [scanHistory, setScanHistory] = useState<ScanHistoryItem[]>([]);
  const [totalScans, setTotalScans] = useState(0);
  const [successfulScans, setSuccessfulScans] = useState(0);

  // Helper function: Load data from localStorage
  const loadFromStorage = useCallback((): PersistedScanData | null => {
    if (typeof window === 'undefined') return null;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const data: PersistedScanData = JSON.parse(stored);

      // Convert timestamps to Date objects
      const historyWithDates = data.history.map((item) => ({
        ...item,
        timestamp: new Date(item.timestamp),
      }));

      // Filter out entries from previous days
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const todaysHistory = historyWithDates.filter((item) => {
        const itemDate = new Date(
          item.timestamp.getFullYear(),
          item.timestamp.getMonth(),
          item.timestamp.getDate(),
        );
        return itemDate.getTime() === today.getTime();
      });

      // If we filtered out entries, reset stats since they're no longer accurate
      const statsNeedReset = todaysHistory.length < historyWithDates.length;

      if (statsNeedReset) {
        console.log(
          `Filtered out ${historyWithDates.length - todaysHistory.length} scan(s) from previous days`,
        );
      }

      return {
        ...data,
        history: todaysHistory,
        totalScans: statsNeedReset ? todaysHistory.length : data.totalScans,
        successfulScans: statsNeedReset
          ? todaysHistory.filter((h) => h.hasOrders).length
          : data.successfulScans,
      };
    } catch (error) {
      console.warn('Failed to load scan history from localStorage:', error);
      return null;
    }
  }, []);

  // Helper function: Save data to localStorage
  const saveToStorage = useCallback((data: PersistedScanData) => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save scan history to localStorage:', error);
    }
  }, []);

  // Helper function: Check if cleanup is needed
  const shouldCleanup = useCallback((history: ScanHistoryItem[]): boolean => {
    if (history.length === 0) return false;

    // Find the newest entry
    const newestEntry = history.reduce((newest, current) =>
      current.timestamp > newest.timestamp ? current : newest,
    );

    const hoursSinceNewest = (Date.now() - newestEntry.timestamp.getTime()) / (1000 * 60 * 60);
    return hoursSinceNewest > CLEANUP_THRESHOLD_HOURS;
  }, []);

  // Helper function: Clean up storage
  const cleanupStorage = useCallback(() => {
    if (typeof window === 'undefined') return;

    localStorage.removeItem(STORAGE_KEY);
    setScanHistory([]);
    setTotalScans(0);
    setSuccessfulScans(0);
  }, []);

  // Initial load and cleanup check
  useEffect(() => {
    const stored = loadFromStorage();

    if (!stored) {
      // No stored data
      return;
    }

    if (shouldCleanup(stored.history)) {
      // Data is too old - clean up
      console.log('Cleaning up old scan history (older than 1 hour)');
      cleanupStorage();
      return;
    }

    // Load data (already filtered by date in loadFromStorage)
    setScanHistory(stored.history);
    setTotalScans(stored.totalScans);
    setSuccessfulScans(stored.successfulScans);
  }, [loadFromStorage, shouldCleanup, cleanupStorage]);

  // Save data when state changes
  useEffect(() => {
    if (scanHistory.length === 0 && totalScans === 0) {
      // Empty state - don't save
      return;
    }

    const dataToSave: PersistedScanData = {
      history: scanHistory,
      totalScans,
      successfulScans,
      lastActivity: new Date().toISOString(),
    };

    saveToStorage(dataToSave);
  }, [scanHistory, totalScans, successfulScans, saveToStorage]);

  // Update scan history
  const updateScanHistory = useCallback(
    (next: ScanHistoryItem[] | ((prev: ScanHistoryItem[]) => ScanHistoryItem[])) => {
      setScanHistory((prev) =>
        (typeof next === 'function' ? next(prev) : next).slice(0, MAX_HISTORY_ITEMS),
      );
    },
    [],
  );

  // Update statistics
  const updateStats = useCallback((newTotalScans: number, newSuccessfulScans: number) => {
    setTotalScans(newTotalScans);
    setSuccessfulScans(newSuccessfulScans);
  }, []);

  // Reset everything
  const clearAll = useCallback(() => {
    cleanupStorage();
  }, [cleanupStorage]);

  // Debug info for development
  const getDebugInfo = useCallback(() => {
    const stored = loadFromStorage();
    return {
      hasStoredData: !!stored,
      itemCount: scanHistory.length,
      oldestEntry:
        scanHistory.length > 0
          ? Math.min(...scanHistory.map((item) => item.timestamp.getTime()))
          : null,
      newestEntry:
        scanHistory.length > 0
          ? Math.max(...scanHistory.map((item) => item.timestamp.getTime()))
          : null,
      wouldCleanup: stored ? shouldCleanup(stored.history) : false,
    };
  }, [loadFromStorage, scanHistory, shouldCleanup]);

  // Cleanup interval
  useEffect(() => {
    const interval = setInterval(() => {
      setScanHistory((prevScanHistory: ScanHistoryItem[]) => {
        return prevScanHistory.filter((item) => {
          const hoursSince = (Date.now() - item.timestamp.getTime()) / 1000 / 60 / 60;
          return hoursSince <= CLEANUP_THRESHOLD_HOURS;
        });
      });
    }, CLEANUP_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  return {
    scanHistory,
    totalScans,
    successfulScans,
    updateScanHistory,
    updateStats,
    clearAll,
    getDebugInfo, // For development/debugging
  };
}
